/**
 * Geometry and timing for the Autodesk Assistant pinwheel mark, ported from
 * Tyto's `au-2026` branch (`signInLogoAnimation.data.ts` + the four
 * `pinwheel-blade-*.svg` files), generated from Figma node 5378:7971.
 *
 * `AssistantIcon` and `AnimatedAssistantIcon` both read this, so the still mark
 * is the animation's last frame by construction. Sizes are center-anchored
 * scale factors and insets resolve against the 280x280 root, as upstream.
 */

/** Every coordinate in this file is in this space. */
export const LOGO_CANVAS_PX = 280;

export const LOGO_DURATION_MS = 2000;

/** Phase boundaries in ms (Figma `times` 0.375, 0.625, 0.875 of 2000ms). */
export const SWEEP_START_MS = 750;
export const SWEEP_END_MS = 1750;
export const COLLAPSE_END_MS = 1250;
export const SPROUT_START_MS = 1250;

export const EASE_SWEEP = 'cubic-bezier(0.6, 0, 0.4, 1)';
export const EASE_SPROUT = 'cubic-bezier(0.4, 1, 0.6, 1)';
export const EASE_DIM = 'cubic-bezier(0.5, 0, 0.5, 1)';
export const EASE_COLLAPSE = 'cubic-bezier(0.4, 0, 0.6, 0)';

/** Rotation pivot in canvas units (Figma node 5378:7972). */
export const ROTATION_PIVOT_X = 140;
export const ROTATION_PIVOT_Y = 136;
export const ROTATION_FROM_DEG = -90;

export interface LogoDot {
  /** Figma node id, for diffing against the source. */
  node: string;
  left: number;
  top: number;
  size: number;
  /** Fraction of the timeline where it snaps from hidden to visible. */
  popAt: number;
  /** Opacity it settles on. 1 means it never dims. */
  endOpacity: number;
  /** Scale + travel across the sweep window. Absent for dots that hold still. */
  sweep?: {
    from: number;
    to: number;
    fromX: number;
    fromY: number;
    toX: number;
    toY: number;
  };
  /** Scale the dot collapses to, clearing space for a blade. */
  collapseTo?: number;
}

export type BladeSlug = 'top' | 'left' | 'right' | 'bottom';

export interface LogoBlade {
  node: string;
  slug: BladeSlug;
  left: number;
  top: number;
  width: number;
  height: number;
  /** Squashed start scale it stretches out of. */
  fromScaleX: number;
  fromScaleY: number;
  /** Travel across the sprout window, via a mid keyframe. */
  fromX: number;
  fromY: number;
  midX: number;
  midY: number;
  toX: number;
  toY: number;
  /** Easing on the second half of the travel. */
  slideEase: string;
}

/** Each `d` is authored in its own box, so a blade needs only a translate. */
export const BLADE_PATHS: Record<BladeSlug, string> = {
  top: 'M44.8305 22.4152C44.8305 27.4627 42.3984 31.5 40.3317 35.8564L40.3479 35.8642L0 116.551V22.4152L0.119445 20.122C1.26669 8.82025 10.8111 0.000695489 22.4153 0C34.7945 -1.72152e-06 44.8305 10.0364 44.8305 22.4152Z',
  left: 'M22.4152 0C27.4627 0 35.8564 4.49881 35.8564 4.49881L35.8642 4.48256L116.551 44.8297H22.4152L20.1221 44.7111C8.82026 43.563 0.000693875 34.0194 0 22.4144C0 10.036 10.0364 0 22.4152 0Z',
  right:
    'M94.1354 44.8305C89.0882 44.8305 85.0518 42.4141 80.6943 40.3317C62.6058 31.6876 24.6394 12.321 0 0H94.1354L96.4283 0.119445C107.73 1.26669 116.55 10.8111 116.551 22.4153C116.551 34.7937 106.515 44.8305 94.1354 44.8305Z',
  bottom:
    'M0 94.1354C0 89.0882 4.49881 80.6943 4.49881 80.6943L4.48256 80.6861L44.8297 0V94.1354L44.7111 96.4283C43.563 107.73 34.0194 116.55 22.4152 116.551C10.036 116.551 0 106.515 0 94.1354Z',
};

export const LOGO_DOTS: LogoDot[] = [
  {
    node: '5378:7977',
    left: 131.01,
    top: 68,
    size: 20,
    popAt: 0.0675,
    endOpacity: 1,
    sweep: { from: 1, to: 2.2, fromX: 0, fromY: 3, toX: -1, toY: -43 },
  },
  {
    node: '5378:7978',
    left: 76,
    top: 129,
    size: 20,
    popAt: 0.2249,
    endOpacity: 1,
    sweep: { from: 1, to: 2.2, fromX: -1, fromY: 0, toX: -49, toY: 0 },
  },
  {
    node: '5378:7979',
    left: 184.99,
    top: 129,
    size: 20,
    popAt: 0.2527,
    endOpacity: 1,
    sweep: { from: 1, to: 2.2, fromX: 0, fromY: 0, toX: 58, toY: 0 },
  },
  {
    node: '5378:7980',
    left: 131.01,
    top: 182,
    size: 20,
    popAt: 0.3246,
    endOpacity: 1,
    sweep: { from: 1, to: 2.2, fromX: 0.82, fromY: 0.46, toX: 0, toY: 56 },
  },
  {
    node: '5378:7984',
    left: 68,
    top: 64,
    size: 36,
    popAt: 0.1335,
    endOpacity: 0.6,
    sweep: { from: 0.556, to: 1, fromX: -1, fromY: -1, toX: 0, toY: 0 },
  },
  {
    node: '5378:7985',
    left: 178,
    top: 64,
    size: 36,
    popAt: 0.2527,
    endOpacity: 0.6,
    sweep: { from: 0.556, to: 1, fromX: -1, fromY: -1, toX: 0, toY: 0 },
  },
  {
    node: '5378:7988',
    left: 68,
    top: 174,
    size: 36,
    popAt: 0.1995,
    endOpacity: 0.6,
    sweep: { from: 0.556, to: 1, fromX: -1, fromY: -1, toX: 0, toY: 0 },
  },
  {
    node: '5378:7989',
    left: 178,
    top: 174,
    size: 36,
    popAt: 0.1108,
    endOpacity: 0.6,
    sweep: { from: 0.556, to: 1, fromX: -1, fromY: -1, toX: 0, toY: 0 },
  },
  {
    node: '5378:7981',
    left: 75.01,
    top: 20,
    size: 20,
    popAt: 0.1516,
    endOpacity: 0.5,
  },
  {
    node: '5378:7982',
    left: 184.99,
    top: 20,
    size: 20,
    popAt: 0.3744,
    endOpacity: 0.5,
  },
  {
    node: '5378:7983',
    left: 22,
    top: 71,
    size: 20,
    popAt: 0.0899,
    endOpacity: 0.5,
  },
  {
    node: '5378:7986',
    left: 238,
    top: 71,
    size: 20,
    popAt: 0.2757,
    endOpacity: 0.5,
  },
  {
    node: '5378:7987',
    left: 22,
    top: 181,
    size: 20,
    popAt: 0.1575,
    endOpacity: 0.5,
  },
  {
    node: '5378:7990',
    left: 238,
    top: 181,
    size: 20,
    popAt: 0.0077,
    endOpacity: 0.5,
  },
  {
    node: '5378:7991',
    left: 75.01,
    top: 232,
    size: 20,
    popAt: 0.0552,
    endOpacity: 0.5,
  },
  {
    node: '5378:7992',
    left: 184.99,
    top: 232,
    size: 20,
    popAt: 0.2446,
    endOpacity: 0.5,
  },
  {
    node: '5378:7993',
    left: 22,
    top: 20,
    size: 20,
    popAt: 0.1942,
    endOpacity: 0.5,
    collapseTo: 0,
  },
  {
    node: '5378:7994',
    left: 131.01,
    top: 20,
    size: 20,
    popAt: 0.1434,
    endOpacity: 0.5,
    collapseTo: 0,
  },
  {
    node: '5378:7995',
    left: 238,
    top: 20,
    size: 20,
    popAt: 0.2989,
    endOpacity: 0.5,
    collapseTo: 0,
  },
  {
    node: '5378:7996',
    left: 22,
    top: 129,
    size: 20,
    popAt: 0.0242,
    endOpacity: 0.5,
    collapseTo: 0,
  },
  {
    node: '5378:7997',
    left: 131.01,
    top: 129,
    size: 20,
    popAt: 0.1259,
    endOpacity: 0.5,
    collapseTo: 0.05,
  },
  {
    node: '5378:7998',
    left: 238,
    top: 129,
    size: 20,
    popAt: 0.0957,
    endOpacity: 0.5,
    collapseTo: 0,
  },
  {
    node: '5378:7999',
    left: 22,
    top: 232,
    size: 20,
    popAt: 0.2645,
    endOpacity: 0.5,
    collapseTo: 0,
  },
  {
    node: '5378:8000',
    left: 131.01,
    top: 232,
    size: 20,
    popAt: 0.3246,
    endOpacity: 0.5,
    collapseTo: 0,
  },
  {
    node: '5378:8001',
    left: 238,
    top: 232,
    size: 20,
    popAt: 0.0467,
    endOpacity: 0.5,
    collapseTo: 0,
  },
];

export const LOGO_BLADES: LogoBlade[] = [
  {
    node: '5378:7973',
    slug: 'top',
    left: 117.99,
    top: 22.26,
    width: 44.8305,
    height: 116.551,
    fromScaleX: 0.6,
    fromScaleY: 0.2,
    fromX: -2.34,
    fromY: -13.48,
    midX: -0.401,
    midY: -9.325,
    toX: 0,
    toY: -9.259,
    slideEase: EASE_SPROUT,
  },
  {
    node: '5378:7974',
    slug: 'left',
    left: 26.68,
    top: 117.01,
    width: 116.551,
    height: 44.8297,
    fromScaleX: 0.2,
    fromScaleY: 0.6,
    fromX: -17.081,
    fromY: 1.887,
    midX: -10.071,
    midY: -0.88,
    toX: -12.673,
    toY: 0,
    slideEase: EASE_SPROUT,
  },
  {
    node: '5378:7975',
    slug: 'right',
    left: 125.66,
    top: 124.88,
    width: 116.551,
    height: 44.8305,
    fromScaleX: 0.2,
    fromScaleY: 0.6,
    fromX: 31.702,
    fromY: -10.937,
    midX: 30.962,
    midY: -7.96,
    toX: 32.326,
    toY: -7.875,
    slideEase: EASE_SPROUT,
  },
  {
    node: '5378:7976',
    slug: 'bottom',
    left: 109.37,
    top: 118.1,
    width: 44.8297,
    height: 116.551,
    fromScaleX: 0.6,
    fromScaleY: 0.2,
    fromX: 11.735,
    fromY: 33.519,
    midX: 9.41,
    midY: 35.121,
    toX: 8.621,
    toY: 34.905,
    slideEase: 'linear',
  },
];

export interface RestingDot {
  cx: number;
  cy: number;
  r: number;
  opacity: number;
}

/** A dot's last frame: center-anchored scale plus the sweep's travel. */
export const restingDot = (dot: LogoDot): RestingDot => {
  const scale = dot.sweep?.to ?? dot.collapseTo ?? 1;
  return {
    cx: dot.left + dot.size / 2 + (dot.sweep?.toX ?? 0),
    cy: dot.top + dot.size / 2 + (dot.sweep?.toY ?? 0),
    r: (dot.size / 2) * scale,
    opacity: dot.endOpacity,
  };
};
